import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { installationAPI } from '../api/installation';
import { toast } from 'react-toastify';
import './CreateInstallation.css';

const CreateInstallation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    installationName: '',
    description: '',
    computerName: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [showActivationModal, setShowActivationModal] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await installationAPI.create(formData);
      if (response.success) {
        setActivationCode(response.data.activationCode);
        setShowActivationModal(true);
        toast.success('Installation created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create installation');
    } finally {
      setLoading(false);
    }
  };

  const copyActivationCode = () => {
    navigator.clipboard.writeText(activationCode);
    toast.success('Activation code copied to clipboard!');
  };

  const handleClose = () => {
    setShowActivationModal(false);
    navigate('/installations');
  };

  return (
    <div className="create-installation-container">
      <div className="page-header">
        <div>
          
        <h1>Create New Installation</h1>
        <p>Set up a new installation to generate an activation code for your Windows application</p>
        </div>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit} className="installation-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="installationName">
                Installation Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="installationName"
                name="installationName"
                className="form-input"
                value={formData.installationName}
                onChange={handleChange}
                required
                placeholder="Enter a unique name for this installation"
              />
              <small className="help-text">
                Choose a descriptive name to easily identify this installation
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="computerName">Computer Name</label>
              <input
                type="text"
                id="computerName"
                name="computerName"
                className="form-input"
                value={formData.computerName}
                onChange={handleChange}
                placeholder="Enter the computer name (optional)"
              />
              <small className="help-text">
                The name of the computer where this installation will be used
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add any additional details about this installation"
              />
              <small className="help-text">
                Optional notes or details about this installation
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/installations')}
              className="btn btn-secondary"
              disabled={loading}
            >
              <span>←</span> Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <span>✓</span> {loading ? 'Creating...' : 'Create Installation'}
            </button>
          </div>
        </form>
      </div>

      {showActivationModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>🎉 Installation Created Successfully!</h2>
            </div>
            <div className="modal-body">
              <div className="activation-info">
                <p className="info-text">
                  Share this activation code with the user to activate the Windows software:
                </p>
                <div className="activation-code-display">
                  <code className="activation-code">{activationCode}</code>
                  <button onClick={copyActivationCode} className="btn-copy">
                    📋 Copy Code
                  </button>
                </div>
                <div className="activation-instructions">
                  <h3>Next Steps:</h3>
                  <ol>
                    <li>Copy the activation code using the button above</li>
                    <li>Share this code with the end user securely</li>
                    <li>The user should enter this code in the Windows application</li>
                    <li>Once activated, the installation will be linked to their computer</li>
                  </ol>
                </div>
                <div className="warning-box">
                  <strong>⚠️ Important:</strong>
                  Make sure to save this activation code. You won't be able to see it again after closing this window.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleClose} className="btn btn-primary">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInstallation;